import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";
import {
  useDeleteTriggerMutation,
  useLazyViewExecutedTriggersQuery,
  useViewTriggersQuery,
} from "../../redux/api/trigger-api/trigger-api";
import TriggerSideMenu from "../../shared/components/alert-side-menu/trigger-side-menu";
import ExectutedTriggerCard from "../../shared/components/exectuted-trigger-card/exectuted-trigger-card";
import DeleteConfirmationModal from "../../shared/components/modals/delete-confirmation-modal/delete-confirmation-modal";
import Pagination from "../../shared/components/pagination/pagination";
import Tabs from "../../shared/components/tabs/tabs";
import TriggerCard from "../../shared/components/trigger-card/trigger-card";
import { ExecutedTrigger, Trigger } from "../../shared/interfaces/modals";
import Button from "../../shared/ui-elements/button/button";
import { showFailureToast, showSuccessToast } from "../../shared/utils";
import { Pages } from "../routes";
import styles from "./manage-triggers.module.scss";

const ITEMS_PER_PAGE = 10;

interface ILocationState {
  selectedTab: number;
}

const ManageTriggers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState: ILocationState = location.state as ILocationState;
  const [showSideMenuState, setShowSideMenuState] = useState(true);
  const [selectedId, setSelectedId] = useState(1);
  const [isShowTriggerDeleteModal, setIsShowTriggerDeleteModal] =
    useState(false);
  const [triggerDeleteId, setTriggerDeleteId] = useState("0");
  const [currentPage, setCurrentPage] = useState(0);

  const [executedTriggersLength, setExecutedTriggersLength] = useState(0);
  const [currentExecutedPage, setCurrentExecutedPage] = useState(0);
  const [executedTriggers, setExecutedTriggers] = useState<ExecutedTrigger[]>(
    []
  );

  const { data: triggers } = useViewTriggersQuery({
    page: currentPage,
    size: ITEMS_PER_PAGE,
  });
  const [executedTriggerGetTriggers] = useLazyViewExecutedTriggersQuery();
  const [deleteTrigger, { isLoading: isDeletingTrigger }] =
    useDeleteTriggerMutation();

  const onShowTriggerDeleteModal = (triggerId: string) => {
    setTriggerDeleteId(triggerId);
    setIsShowTriggerDeleteModal(true);
  };

  useEffect(() => {
    if (locationState && locationState.selectedTab) {
      setSelectedId(locationState.selectedTab);
    }
  }, [locationState]);

  useEffect(() => {
    const fetchExecutedTriggers = () => {
      executedTriggerGetTriggers({
        page: currentExecutedPage,
        size: ITEMS_PER_PAGE,
      })
        .unwrap()
        .then((res) => {
          setExecutedTriggers(res.triggers);
          setExecutedTriggersLength(res.totalElements);
        })
        .catch(() => {
          setExecutedTriggers([]);
          setExecutedTriggersLength(0);
        });
    };

    fetchExecutedTriggers();

    const intervalId = setInterval(fetchExecutedTriggers, 3000);

    return () => clearInterval(intervalId);
  }, [currentExecutedPage, executedTriggerGetTriggers]);

  const onDelete = async () => {
    setIsShowTriggerDeleteModal(false);

    if (triggerDeleteId) {
      await deleteTrigger(triggerDeleteId)
        .unwrap()
        .then(() => {
          showSuccessToast("Trigger deleted successfully");
        })
        .catch(() => {
          showFailureToast("Failed to delete the trigger");
        });
    }
  };

  const onEdit = (trigger: Trigger) => {
    navigate(Pages.updateAlert, {
      state: {
        id: trigger.id,
        label: trigger.label,
        deviceId: trigger.deviceId,
        durationInMinutes: trigger.durationInMinutes,
        recipientIds: trigger.triggerActions[0].additionalFields.recipientIds,
        action: trigger.triggerActions[0].type,
        rules: trigger.triggerRules.map((rule) => {
          return {
            sensorParamDefId: rule.sensorParamDefId,
            condition: rule.condition,
            value: rule.value,
          };
        }),
      },
    });
  };

  return (
    <>
      <Row className="align-items-center">
        <Col xs={12} md={9}>
          <Tabs />
        </Col>
      </Row>
      <div className={`${styles.alertContainer} mt-4 d-block d-md-flex`}>
        <div className={`${styles.menu} ${!showSideMenuState && styles.hide}`}>
          <TriggerSideMenu
            setShowSideMenuState={setShowSideMenuState}
            selectedId={selectedId}
            onClick={(id) => setSelectedId(id)}
            items={[
              { id: 1, itemName: "Executed Triggers" },
              { id: 2, itemName: "Manage Triggers" },
            ]}
          />
        </div>
        <div className={`${styles.content} ${showSideMenuState && `ms-3`} p-4`}>
          <Row>
            <Col>
              {selectedId === 1 ? (
                <>
                  <div className={styles.title}>Executed Triggers</div>
                  <Row className="mt-4">
                    {executedTriggers?.map((a) => (
                      <Col xs={12} className="mb-3" key={uuid()}>
                        <ExectutedTriggerCard
                          executedTrigger={a}
                          onDelete={() => {}}
                        />
                      </Col>
                    ))}
                  </Row>
                  <Row>
                    <Col>
                      <Pagination
                        length={executedTriggersLength}
                        itemsPerPage={ITEMS_PER_PAGE}
                        currentPage={currentExecutedPage + 1}
                        updateCurrentPage={(executedPageNumber: number) => {
                          setCurrentExecutedPage(executedPageNumber - 1);
                        }}
                      />
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  <Row className="align-items-center justify-content-between">
                    <Col className={`${styles.title} col-12 col-sm`}>
                      Manage Triggers
                    </Col>
                    <Col className="col-12 col-sm-5 col-lg-4 col-xl-3 mt-3 mt-sm-0">
                      <Button
                        text="New Trigger"
                        onClick={() => navigate(Pages.addAlert)}
                        variant="Primary"
                        borderRadius={24}
                        padding="8px 30px"
                        type="button"
                      />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    {triggers &&
                      triggers.triggers.map((a) => (
                        <Col xs={12} className="mb-3" key={uuid()}>
                          <TriggerCard
                            trigger={a}
                            onDelete={onShowTriggerDeleteModal}
                            onEdit={onEdit}
                          />
                        </Col>
                      ))}
                  </Row>
                  <Row>
                    <Col>
                      <Pagination
                        length={triggers ? triggers.totalElements : 0}
                        itemsPerPage={ITEMS_PER_PAGE}
                        currentPage={currentPage + 1}
                        updateCurrentPage={(pageNumber: number) => {
                          setCurrentPage(pageNumber - 1);
                        }}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </Col>
          </Row>
        </div>
      </div>
      <DeleteConfirmationModal
        show={isShowTriggerDeleteModal}
        onClose={() => setIsShowTriggerDeleteModal(false)}
        onConfirm={onDelete}
        title="Remove Trigger"
        description="Are you sure you want to remove this trigger?"
        isLoading={isDeletingTrigger}
      />
    </>
  );
};

export default ManageTriggers;
